import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { createPortal } from 'react-dom';

interface CustomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  footer?: React.ReactNode;
  className?: string;
  maxHeight?: string;
  width?: string;
  position?: 'bottom' | 'right';
  
  // Container adaptation props
  containerSelector?: string;
  constrainToContainer?: boolean;
  backdropScope?: 'viewport' | 'container' | 'none';
  relativeTo?: 'viewport' | 'container';
  portalTarget?: Element | null;
  adaptWidth?: boolean;
  adaptHeight?: boolean;
}

const CustomDrawer: React.FC<CustomDrawerProps> = ({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  footer,
  className = '',
  maxHeight = '60vh',
  width = '400px',
  position = 'bottom',
  
  // Container adaptation props
  containerSelector,
  constrainToContainer = false,
  backdropScope = 'viewport',
  relativeTo = 'viewport',
  portalTarget,
  adaptWidth = false,
  adaptHeight = false
}) => {
  const [containerBounds, setContainerBounds] = useState<DOMRect | null>(null);
  const containerRef = useRef<Element | null>(null);

  // Container detection and measurement
  useEffect(() => {
    if (!constrainToContainer || !containerSelector) return;

    const findContainer = () => {
      const container = document.querySelector(containerSelector);
      if (container) {
        containerRef.current = container;
        setContainerBounds(container.getBoundingClientRect());
      }
    };

    const handleResize = () => {
      if (containerRef.current) {
        setContainerBounds(containerRef.current.getBoundingClientRect());
      }
    };

    findContainer();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerSelector, constrainToContainer, isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Trap focus within drawer
      const focusableElements = document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      firstElement?.focus();
    }
  }, [isOpen]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Only prevent body scroll for viewport-level drawers
      if (backdropScope === 'viewport') {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (backdropScope === 'viewport') {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, onClose, backdropScope]);

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const shouldClose = info.velocity.y > 500 || info.offset.y > 100;
    if (shouldClose) {
      onClose();
    }
  };

  const backdropVariants = {
    open: {
      opacity: 1,
      backdropFilter: 'blur(2px)',
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    closed: {
      opacity: 0,
      backdropFilter: 'blur(0px)',
      transition: {
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const drawerVariants = {
    open: {
      y: 0,
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300,
        mass: 0.8
      }
    },
    closed: {
      y: '100%',
      scale: 0.95,
      opacity: 0,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 400,
        mass: 0.6
      }
    }
  };

  const rightDrawerVariants = {
    open: {
      x: 0,
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300,
        mass: 0.8
      }
    },
    closed: {
      x: '100%',
      scale: 0.95,
      opacity: 0,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 400,
        mass: 0.6
      }
    }
  };

  const handleVariants = {
    rest: { scale: 1, opacity: 0.6 },
    hover: { 
      scale: 1.1, 
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10
      }
    },
    tap: { 
      scale: 0.95,
      transition: {
        type: 'spring',
        stiffness: 600,
        damping: 15
      }
    }
  };

  // Calculate positioning and dimensions
  const getDrawerStyles = (): {
    positioning: string;
    width: string | number;
    height: string | number;
    left?: string | number;
    right?: string | number;
    top?: string | number;
    bottom?: string | number;
  } => {
    if (!constrainToContainer || !containerBounds) {
      // Default behavior
      return {
        positioning: position === 'right' 
          ? 'fixed right-0 top-0 h-full'
          : 'fixed bottom-0 left-1/2 -translate-x-1/2',
        width: position === 'right' ? width : `w-full max-w-[${width}]`,
        height: position === 'bottom' ? `max-h-[${maxHeight}]` : 'h-full'
      };
    }

    // Container-constrained positioning
    const adaptedWidth = adaptWidth ? containerBounds.width : parseInt(width.replace('px', ''));
    const adaptedHeight = adaptHeight ? containerBounds.height * 0.6 : parseInt(maxHeight.replace('vh', '')) * window.innerHeight / 100;

    if (position === 'bottom') {
      return {
        positioning: 'fixed',
        width: adaptedWidth,
        height: adaptedHeight,
        left: containerBounds.left,
        bottom: 0
      };
    } else {
      return {
        positioning: 'fixed',
        width: adaptedWidth,
        height: adaptedHeight ? adaptedHeight : window.innerHeight,
        right: window.innerWidth - containerBounds.right,
        top: containerBounds.top
      };
    }
  };

  // Calculate backdrop styles
  const getBackdropStyles = () => {
    if (backdropScope === 'none') return { display: 'none' };
    
    if (backdropScope === 'container' && containerBounds) {
      return {
        position: 'fixed' as const,
        left: `${containerBounds.left}px`,
        top: `${containerBounds.top}px`,
        width: `${containerBounds.width}px`,
        height: `${containerBounds.height}px`,
        zIndex: 40
      };
    }

    // Default viewport backdrop
    return {
      position: 'absolute' as const,
      inset: '0',
      zIndex: 40
    };
  };

  const drawerStyles = getDrawerStyles();
  const backdropStyles = getBackdropStyles();
  const portalContainer = portalTarget || document.body;

  const drawer = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className={`${constrainToContainer && containerBounds ? 'absolute' : 'fixed'} inset-0 z-50`}>
          {/* Backdrop */}
          {backdropScope !== 'none' && (
            <motion.div
              className="bg-transparent"
              style={backdropStyles}
              variants={backdropVariants}
              initial="closed"
              animate="open"
              exit="closed"
              onClick={onClose}
            />
          )}
          
          {/* Drawer Content */}
          <motion.div
            className={`
              bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl
              ${position === 'right' ? 'rounded-l-2xl' : 'rounded-t-2xl'}
              flex flex-col overflow-hidden z-50
              ${className}
            `}
            style={{
              position: 'fixed',
              left: typeof drawerStyles.left === 'number' ? `${drawerStyles.left}px` : drawerStyles.left,
              right: typeof drawerStyles.right === 'number' ? `${drawerStyles.right}px` : drawerStyles.right,
              top: typeof drawerStyles.top === 'number' ? `${drawerStyles.top}px` : drawerStyles.top,
              bottom: typeof drawerStyles.bottom === 'number' ? `${drawerStyles.bottom}px` : drawerStyles.bottom,
              width: typeof drawerStyles.width === 'number' ? `${drawerStyles.width}px` : drawerStyles.width,
              height: typeof drawerStyles.height === 'number' ? `${drawerStyles.height}px` : drawerStyles.height,
              maxHeight: !constrainToContainer ? maxHeight : undefined,
              transform: !constrainToContainer && position === 'bottom' ? 'translateX(-50%)' : undefined,
              willChange: 'transform'
            }}
            variants={position === 'right' ? rightDrawerVariants : drawerVariants}
            initial="closed"
            animate="open"
            exit="closed"
            drag={position === 'bottom' ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 300 }}
            dragElastic={{ top: 0, bottom: 0.3 }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag Handle (only for bottom drawers) */}
            {position === 'bottom' && (
              <motion.div
                className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
                variants={handleVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
              >
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
              </motion.div>
            )}
            
            {/* Header */}
            {(title || subtitle) && (
              <div className="px-6 py-4 border-b border-gray-200/50">
                {title && (
                  <h3 className="text-lg font-semibold text-gray-900">
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="text-sm text-gray-500 mt-1">
                    {subtitle}
                  </p>
                )}
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {children}
            </div>
            
            {/* Footer */}
            {footer && (
              <div className="px-6 py-4 border-t border-gray-200/50 bg-white/50">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(drawer, portalContainer);
};

export default CustomDrawer;